if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.mainCarousel = this.dataset.mainCarousel ? this.querySelector(this.dataset.mainCarousel) : null;
        this.thumbCarousel = this.dataset.thumbCarousel ? this.querySelector(this.dataset.thumbCarousel) : null;

        this.querySelectorAll('.product-gallery__thumb').forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => this.setActiveMedia(thumbnail.dataset.target));
        });

        // The main carousel's own 'slideChange' event is the single source of
        // truth for which media is active, no matter what caused the change —
        // a drag, the prev/next arrows, or setActiveMedia()'s own slideTo()
        // call below. Driving the thumbnail highlight/scroll from this one
        // listener (instead of duplicating that logic at every call site)
        // keeps the two carousels from ever drifting out of sync with each
        // other.
        this.onSlideChange = this.onSlideChange.bind(this);
        if (this.mainCarousel) {
          if (this.mainCarousel.swiper) {
            this.mainCarousel.swiper.on('slideChange', this.onSlideChange);
          } else {
            this.mainCarousel.addEventListener(
              'swiper-carousel:ready',
              () => this.mainCarousel.swiper.on('slideChange', this.onSlideChange),
              { once: true }
            );
          }
        }
      }

      onSlideChange() {
        // Scoped to .product-gallery__slide, not just [data-media-id] — video
        // and 3D-model slides (product-thumbnail.liquid) nest their own
        // <deferred-media>/<product-model> with a data-media-id of their own,
        // which would otherwise inflate this list and shift every index after
        // the first one out of alignment with the thumbnail strip.
        const items = Array.from(this.querySelectorAll('.product-gallery__slide[data-media-id]'));
        if (!items.length) return;
        this.applyActiveMedia(items, this.mainCarousel.swiper.activeIndex);
      }

      // Called by global.js's ProductInfo.updateMedia() whenever the selected
      // variant changes, so this method name/signature is a public contract —
      // keep it even if the internals below change. Applies the active state
      // directly rather than waiting on the main carousel's 'slideChange'
      // event to round-trip back to us — this is the thing the user clicked,
      // so it shouldn't depend on Swiper deciding to fire an event for it.
      // 'slideChange' (below) is still what keeps things in sync for the
      // prev/next arrows and dragging, which have no call site of their own.
      setActiveMedia(mediaId) {
        const items = Array.from(this.querySelectorAll('.product-gallery__slide[data-media-id]'));
        if (!items.length) return;

        let index = items.findIndex((item) => item.dataset.mediaId === mediaId);
        if (index === -1) index = 0;

        if (this.mainCarousel && this.mainCarousel.swiper) {
          // The variant-change DOM patch (global.js) can add/remove/reorder
          // slides right before calling this, so refresh Swiper's slide cache
          // before asking it to move.
          this.mainCarousel.swiper.update();
          this.mainCarousel.swiper.slideTo(index, 0);
        } else {
          // Swiper hasn't loaded (or hasn't initialized) yet — the shared
          // swiper-carousel.css fallback already renders this as a native
          // scroll-snap row, so just scroll it directly.
          items[index].scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'instant' });
        }

        this.applyActiveMedia(items, index);
      }

      applyActiveMedia(items, index) {
        const activeItem = items[index];
        if (!activeItem) return;

        items.forEach((item) => item.classList.remove('is-active'));
        activeItem.classList.add('is-active');

        this.syncThumbnail(index);
        this.preventStickyHeader();
        this.playActiveMedia(activeItem);
        this.announceLiveRegion(activeItem, index + 1);
      }

      // Keeps the thumbnail strip's highlight and scroll position matched to
      // `index`, regardless of whether the active slide changed via a
      // thumbnail click, a drag, or the prev/next arrows. Driven explicitly
      // here (rather than via Swiper's Thumbs module) so it stays correct
      // even though the two carousels show a different number of slides per
      // view.
      syncThumbnail(index) {
        const thumbs = Array.from(this.querySelectorAll('.product-gallery__thumb'));
        if (!thumbs.length) return;
        const activeThumb = thumbs[index];
        if (!activeThumb) return;

        thumbs.forEach((thumb) => thumb.classList.remove('swiper-slide-thumb-active'));
        activeThumb.classList.add('swiper-slide-thumb-active');

        if (this.thumbCarousel && this.thumbCarousel.swiper) {
          this.thumbCarousel.swiper.slideTo(index);
        } else {
          activeThumb.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      announceLiveRegion(activeItem, position) {
        if (!this.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.liveRegion.setAttribute('aria-hidden', false);
          this.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }
    }
  );
}
